import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { isLive, isOverdue, outstandingByCurrency, sortCurrencies, uninvoicedByCurrency, type MoneyByCurrency } from "@otrack/domain";
import { Button } from "@otrack/ui";
import { EmptyState, money, PageHeader, Panel, rise, Stat } from "../components";
import { useApp, useDataset } from "../context";
import { byRecent, InvoiceRow, withInstallmentsTogether } from "../invoice-list";

const PAID_SHOWN = 6;

/** Amounts in several currencies, stacked, never added together. */
function Amounts({ totals, empty = "None" }: { totals: MoneyByCurrency; empty?: string }) {
  const codes = sortCurrencies(Object.keys(totals).filter((code) => totals[code]));
  if (!codes.length) return <span className="ot-amounts is-empty">{empty}</span>;
  return <span className="ot-amounts">{codes.map((code) => <span key={code}>{money(totals[code]!, code)}</span>)}</span>;
}

export function InvoicesPage() {
  const { now, openNewInvoice } = useApp();
  const dataset = useDataset();
  const [allPaid, setAllPaid] = useState(false);
  const invoices = Object.values(dataset.invoices).filter(isLive).sort(byRecent);
  const drafts = withInstallmentsTogether(invoices.filter((invoice) => invoice.status === "draft"));
  // Overdue first, then by how soon they are due.
  const outstanding = invoices.filter((invoice) => invoice.status === "sent")
    .sort((a, b) => Number(isOverdue(b, now)) - Number(isOverdue(a, now)) || (a.dueDate ?? 0) - (b.dueDate ?? 0));
  const paid = invoices.filter((invoice) => invoice.status === "paid").sort((a, b) => (b.paidAt ?? 0) - (a.paidAt ?? 0));
  const overdue = outstanding.filter((invoice) => isOverdue(invoice, now));
  const uninvoiced = uninvoicedByCurrency(dataset);

  const subtitle = invoices.length
    ? [`${outstanding.length} outstanding`, drafts.length ? `${drafts.length} ${drafts.length === 1 ? "draft" : "drafts"}` : null, overdue.length ? `${overdue.length} overdue` : null].filter(Boolean).join(", ")
    : "Bill clients straight from tracked time.";

  return <div className="ot-page">
    <PageHeader title="Invoices" subtitle={subtitle} actions={<Button tone="primary" icon="plus" onClick={() => openNewInvoice()}>New invoice</Button>}/>
    <motion.div className="ot-kpis" {...rise(1)}>
      <Stat label="Outstanding" value={<Amounts totals={outstandingByCurrency(dataset)} empty="Nothing owed"/>} note={outstanding.length ? `${outstanding.length} sent, not paid yet` : "Tax included"}/>
      <Stat label="Overdue" value={<Amounts totals={outstandingByCurrency(dataset, now)} empty="None"/>} note={overdue.length ? `${overdue.length} past ${overdue.length === 1 ? "its" : "their"} due date` : "Everyone is on time"}/>
      <Stat label="Uninvoiced" value={<Amounts totals={uninvoiced} empty="All billed"/>} note="Earned, not on a sent invoice"/>
    </motion.div>

    {invoices.length === 0 ? <EmptyState
      icon="receipt"
      title="No invoices yet"
      actions={<Button tone="primary" icon="plus" onClick={() => openNewInvoice()}>New invoice</Button>}
    >Pick a client and a period. ontrack drafts the invoice from every uninvoiced entry and finished fixed price item in it.</EmptyState> : <>
      {drafts.length > 0 && <Panel title="Drafts" icon="pencil" description="Still following your time. Nothing is locked yet." aside={<span className="ot-panel__meta">{drafts.length}</span>} index={2}>
        <div className="ot-ilist">{drafts.map((invoice, index) => <InvoiceRow key={invoice.id} invoice={invoice} dataset={dataset} now={now} index={index}/>)}</div>
      </Panel>}
      <Panel title="Outstanding" icon="send" description="Sent and waiting for payment. Overdue ones come first." aside={<span className="ot-panel__meta">{outstanding.length || ""}</span>} index={3}>
        {outstanding.length === 0
          ? <p className="ot-empty">Nothing outstanding. Every sent invoice has been paid.</p>
          : <div className="ot-ilist">{outstanding.map((invoice, index) => <InvoiceRow key={invoice.id} invoice={invoice} dataset={dataset} now={now} index={index}/>)}</div>}
      </Panel>
      {paid.length > 0 && <Panel title="Paid" icon="seal-check" aside={<span className="ot-panel__meta">{paid.length}</span>} index={4}>
        <div className="ot-ilist">
          <AnimatePresence initial={false}>
            {(allPaid ? paid : paid.slice(0, PAID_SHOWN)).map((invoice, index) => <InvoiceRow key={invoice.id} invoice={invoice} dataset={dataset} now={now} index={index}/>)}
          </AnimatePresence>
        </div>
        {paid.length > PAID_SHOWN && <Button tone="ghost" className="ot-more" onClick={() => setAllPaid(!allPaid)}>{allPaid ? "Show fewer" : `Show ${paid.length - PAID_SHOWN} more`}</Button>}
      </Panel>}
    </>}
  </div>;
}
